import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import {
  getClientesConAreas,
  crearCliente,
  editarCliente,
  toggleActivoCliente,
} from '../../services/comercialClienteService';

const ACCENT = '#F97316';

// ── AREA CONFIG ───────────────────────────────────────────────────────────────
const AREA_CFG = {
  tienda: { label: 'TIENDA', color: '#34D399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.25)' },
  distribucion: { label: 'DISTRIBUCIÓN', color: '#60A5FA', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.25)' },
  sala_loncheado: { label: 'SALA LONCHEADO', color: '#D6EF4E', bg: 'rgba(214,239,78,0.12)', border: 'rgba(214,239,78,0.25)' },
};

const AreaBadge = ({ area }) => {
  const cfg = AREA_CFG?.[area];
  if (!cfg) return null;
  return (
    <span className="inline-flex items-center font-oranienbaum text-xs px-2 py-0.5 rounded-full font-semibold tracking-wide whitespace-nowrap"
      style={{ color: cfg?.color, background: cfg?.bg, border: `1px solid ${cfg?.border}` }}>
      {cfg?.label}
    </span>
  );
};

// ── MODAL NUEVO/EDITAR CLIENTE ────────────────────────────────────────────────
const ClienteModal = ({ cliente, onClose, onSaved }) => {
  const isEdit = !!cliente;
  const [form, setForm] = useState({
    nombre: cliente?.nombre || '',
    nombre_comercial: cliente?.nombre_comercial || '',
    tipo_persona: cliente?.tipo_persona || '',
    nif_cif: cliente?.nif_cif || '',
    telefono: cliente?.telefono || '',
    email: cliente?.email || '',
    direccion: cliente?.direccion || '',
    fecha_nacimiento: cliente?.fecha_nacimiento || '',
    notas_comerciales: cliente?.notas_comerciales || '',
    consentimiento_marketing: cliente?.consentimiento_marketing || false,
  });
  const [areas, setAreas] = useState(() => {
    if (!cliente?.cliente_areas) return [];
    return cliente?.cliente_areas?.filter(a => a?.activo)?.map(a => a?.area);
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleArea = (area) => {
    setAreas(prev =>
      prev?.includes(area) ? prev?.filter(a => a !== area) : [...prev, area]
    );
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.nombre?.trim()) { setError('El nombre es obligatorio.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await editarCliente(cliente?.id, { datosGenerales: form, areas });
      } else {
        await crearCliente({ datosGenerales: form, areas, canales: [] });
      }
      onSaved();
    } catch (err) {
      console.error('[ClienteModal] error:', err);
      setError(err?.message || 'Error al guardar el cliente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden max-h-[90vh] flex flex-col"
        style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.25)' }}>
              <Icon name={isEdit ? 'Pencil' : 'UserPlus'} size={15} style={{ color: ACCENT }} />
            </div>
            <h2 className="font-oranienbaum font-bold text-white text-lg">
              {isEdit ? 'Editar Cliente' : 'Nuevo Cliente'}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/80 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {/* DATOS GENERALES */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 rounded-full" style={{ background: ACCENT }} />
              <span className="font-oranienbaum text-white/60 text-xs uppercase tracking-widest">Datos Generales</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Nombre *</label>
                <input value={form?.nombre} onChange={e => handleField('nombre', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
                  placeholder="Nombre del cliente" />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Nombre comercial</label>
                <input value={form?.nombre_comercial} onChange={e => handleField('nombre_comercial', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
                  placeholder="Nombre comercial" />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Tipo persona</label>
                <select value={form?.tipo_persona} onChange={e => handleField('tipo_persona', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
                  <option value="">— Seleccionar —</option>
                  <option value="particular">Particular</option>
                  <option value="empresa">Empresa</option>
                </select>
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">NIF / CIF</label>
                <input value={form?.nif_cif} onChange={e => handleField('nif_cif', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
                  placeholder="NIF / CIF" />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Teléfono</label>
                <input value={form?.telefono} onChange={e => handleField('telefono', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
                  placeholder="Teléfono" />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Email</label>
                <input type="email" value={form?.email} onChange={e => handleField('email', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
                  placeholder="Email" />
              </div>
              <div className="sm:col-span-2">
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Dirección</label>
                <input value={form?.direccion} onChange={e => handleField('direccion', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
                  placeholder="Dirección completa" />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Fecha nacimiento</label>
                <input type="date" value={form?.fecha_nacimiento} onChange={e => handleField('fecha_nacimiento', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }} />
              </div>
              <div className="sm:col-span-2">
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Notas comerciales</label>
                <textarea value={form?.notas_comerciales} onChange={e => handleField('notas_comerciales', e?.target?.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg font-oranienbaum text-sm text-white/90 outline-none resize-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
                  placeholder="Notas internas..." />
              </div>
            </div>
          </div>

          {/* ÁREAS */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 rounded-full" style={{ background: '#60A5FA' }} />
              <span className="font-oranienbaum text-white/60 text-xs uppercase tracking-widest">Áreas</span>
            </div>
            <div className="space-y-2">
              {Object.entries(AREA_CFG)?.map(([key, cfg]) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer group">
                  <div className={`w-5 h-5 rounded flex items-center justify-center transition-all ${areas?.includes(key) ? '' : ''}`}
                    style={areas?.includes(key)
                      ? { background: cfg?.bg, border: `2px solid ${cfg?.color}` }
                      : { background: 'rgba(255,255,255,0.04)', border: '2px solid rgba(255,255,255,0.15)' }}>
                    {areas?.includes(key) && <Icon name="Check" size={11} style={{ color: cfg?.color }} />}
                  </div>
                  <input type="checkbox" className="hidden" checked={areas?.includes(key)}
                    onChange={() => toggleArea(key)} />
                  <span className="font-oranienbaum text-sm" style={{ color: areas?.includes(key) ? cfg?.color : 'rgba(255,255,255,0.55)' }}>
                    {cfg?.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* MARKETING */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 rounded-full" style={{ background: '#A78BFA' }} />
              <span className="font-oranienbaum text-white/60 text-xs uppercase tracking-widest">Marketing</span>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <div className={`w-5 h-5 rounded flex items-center justify-center transition-all`}
                style={form?.consentimiento_marketing
                  ? { background: 'rgba(167,139,250,0.15)', border: '2px solid #A78BFA' }
                  : { background: 'rgba(255,255,255,0.04)', border: '2px solid rgba(255,255,255,0.15)' }}>
                {form?.consentimiento_marketing && <Icon name="Check" size={11} style={{ color: '#A78BFA' }} />}
              </div>
              <input type="checkbox" className="hidden" checked={form?.consentimiento_marketing}
                onChange={e => handleField('consentimiento_marketing', e?.target?.checked)} />
              <span className="font-oranienbaum text-sm text-white/55">
                Autoriza comunicaciones comerciales
              </span>
            </label>
          </div>

          {error && (
            <div className="rounded-lg px-4 py-3 font-oranienbaum text-sm"
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171' }}>
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4"
          style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-lg font-oranienbaum text-sm text-white/50 hover:text-white/80 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg font-oranienbaum text-sm font-semibold transition-all"
            style={{ background: ACCENT, color: '#0A0A0A', opacity: saving ? 0.7 : 1 }}>
            {saving ? <><Icon name="Loader2" size={14} className="animate-spin" /> Guardando…</> : <><Icon name="Check" size={14} />{isEdit ? 'Guardar cambios' : 'Crear cliente'}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── PÁGINA PRINCIPAL ──────────────────────────────────────────────────────────
const FILTROS_AREA = ['Todos', 'Tienda', 'Distribución', 'Sala Loncheado'];
const FILTROS_ESTADO = ['Todos', 'Activos', 'Inactivos'];

const ComercialClientes = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroArea, setFiltroArea] = useState('Todos');
  const [filtroEstado, setFiltroEstado] = useState('Activos');
  const [modalOpen, setModalOpen] = useState(false);
  const [clienteEditar, setClienteEditar] = useState(null);

  const cargarClientes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getClientesConAreas();
      setClientes(data);
    } catch (err) {
      console.error('[ComercialClientes] cargarClientes error:', err);
      setError(err?.message || 'Error al cargar clientes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarClientes(); }, [cargarClientes]);

  // Filtrado
  const clientesFiltrados = clientes?.filter(c => {
    // Filtro estado
    if (filtroEstado === 'Activos' && !c?.activo) return false;
    if (filtroEstado === 'Inactivos' && c?.activo) return false;

    // Filtro área
    const areas = c?.cliente_areas?.filter(a => a?.activo)?.map(a => a?.area) || [];
    if (filtroArea === 'Tienda' && !areas?.includes('tienda')) return false;
    if (filtroArea === 'Distribución' && !areas?.includes('distribucion')) return false;
    if (filtroArea === 'Sala Loncheado' && !areas?.includes('sala_loncheado')) return false;

    // Búsqueda
    if (busqueda?.trim()) {
      const q = busqueda?.toLowerCase();
      const match =
        c?.nombre?.toLowerCase()?.includes(q) ||
        c?.nombre_comercial?.toLowerCase()?.includes(q) ||
        c?.telefono?.toLowerCase()?.includes(q) ||
        c?.email?.toLowerCase()?.includes(q) ||
        c?.nif_cif?.toLowerCase()?.includes(q);
      if (!match) return false;
    }

    return true;
  });

  const handleNuevo = () => { setClienteEditar(null); setModalOpen(true); };
  const handleEditar = (c) => { setClienteEditar(c); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargarClientes(); };

  const handleToggleActivo = async (c) => {
    try {
      await toggleActivoCliente(c?.id, !c?.activo);
      cargarClientes();
    } catch (err) {
      console.error('[ComercialClientes] toggleActivo error:', err);
    }
  };

  const formatFecha = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts)?.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
  };

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <nav className="flex items-center gap-2 text-xs font-oranienbaum text-white/30 mb-2">
            <span>🤝 Comercial</span>
            <Icon name="ChevronRight" size={12} />
            <span className="text-white/60">Clientes</span>
          </nav>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-2xl">👥</span>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Clientes</h1>
            {!loading && (
              <span className="px-2 py-0.5 rounded-full font-oranienbaum text-xs font-semibold"
                style={{ background: 'rgba(249,115,22,0.12)', color: ACCENT, border: '1px solid rgba(249,115,22,0.25)' }}>
                {clientesFiltrados?.length}
              </span>
            )}
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-10">Base unificada · Todas las áreas de negocio</p>
        </div>
        <button onClick={handleNuevo}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-oranienbaum text-sm font-semibold transition-all hover:opacity-90"
          style={{ background: ACCENT, color: '#0A0A0A' }}>
          <Icon name="Plus" size={15} />
          Nuevo Cliente
        </button>
      </div>

      {/* Búsqueda y filtros */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="relative">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            placeholder="Buscar por nombre, nombre comercial, teléfono, email, NIF/CIF…"
            value={busqueda}
            onChange={e => setBusqueda(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-lg font-oranienbaum text-sm text-white/80 placeholder-white/25 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Filtros área */}
          <div className="flex gap-1.5 flex-wrap">
            {FILTROS_AREA?.map(f => (
              <button key={f} onClick={() => setFiltroArea(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all`}
                style={filtroArea === f
                  ? { background: ACCENT, color: '#0A0A0A', fontWeight: 600 }
                  : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
                {f}
              </button>
            ))}
          </div>
          <div className="w-px self-stretch" style={{ background: 'rgba(255,255,255,0.08)' }} />
          {/* Filtros estado */}
          <div className="flex gap-1.5 flex-wrap">
            {FILTROS_ESTADO?.map(f => (
              <button key={f} onClick={() => setFiltroEstado(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all`}
                style={filtroEstado === f
                  ? { background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 600, border: '1px solid rgba(255,255,255,0.20)' }
                  : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.40)' }}>
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-xl px-4 py-3 mb-4 font-oranienbaum text-sm"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#F87171' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Tabla */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        {/* Cabecera */}
        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3"
          style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="col-span-3 font-oranienbaum text-white/40 text-xs uppercase tracking-wide">Cliente</div>
          <div className="col-span-1 font-oranienbaum text-white/40 text-xs uppercase tracking-wide">Tipo</div>
          <div className="col-span-3 font-oranienbaum text-white/40 text-xs uppercase tracking-wide">Áreas</div>
          <div className="col-span-2 font-oranienbaum text-white/40 text-xs uppercase tracking-wide">Contacto</div>
          <div className="col-span-1 font-oranienbaum text-white/40 text-xs uppercase tracking-wide">Última act.</div>
          <div className="col-span-1 font-oranienbaum text-white/40 text-xs uppercase tracking-wide">Estado</div>
          <div className="col-span-1 font-oranienbaum text-white/40 text-xs uppercase tracking-wide text-right">Acc.</div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16" style={{ background: 'rgba(255,255,255,0.01)' }}>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-orange-400 animate-spin" />
              <span className="font-oranienbaum text-white/40 text-sm">Cargando clientes…</span>
            </div>
          </div>
        )}

        {/* Sin resultados */}
        {!loading && !error && clientesFiltrados?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-4"
            style={{ background: 'rgba(255,255,255,0.01)' }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.15)' }}>
              <Icon name="Users" size={24} className="text-orange-400/60" />
            </div>
            <p className="font-oranienbaum text-white/50 font-semibold text-base mb-1">Sin resultados</p>
            <p className="font-oranienbaum text-white/25 text-sm text-center max-w-sm">
              {clientes?.length === 0
                ? 'No hay clientes en la base de datos todavía.' :'Ningún cliente coincide con los filtros actuales.'}
            </p>
          </div>
        )}

        {/* Filas */}
        {!loading && clientesFiltrados?.map((c, idx) => {
          const areasActivas = c?.cliente_areas?.filter(a => a?.activo)?.map(a => a?.area) || [];
          return (
            <div key={c?.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-3.5 cursor-pointer transition-all hover:bg-white/[0.03]"
              style={{ borderBottom: idx < clientesFiltrados?.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}
              onClick={() => navigate(`/comercial-clientes/${c?.id}`)}>

              {/* Cliente */}
              <div className="md:col-span-3 flex flex-col justify-center">
                <span className="font-oranienbaum font-semibold text-white/90 text-sm leading-tight">
                  {c?.nombre}
                </span>
                {c?.nombre_comercial && (
                  <span className="font-oranienbaum text-white/40 text-xs mt-0.5">{c?.nombre_comercial}</span>
                )}
                {c?.nif_cif && (
                  <span className="font-oranienbaum text-white/25 text-xs">{c?.nif_cif}</span>
                )}
              </div>

              {/* Tipo */}
              <div className="md:col-span-1 flex items-center">
                <span className="font-oranienbaum text-white/50 text-xs capitalize">
                  {c?.tipo_persona || '—'}
                </span>
              </div>

              {/* Áreas */}
              <div className="md:col-span-3 flex items-center flex-wrap gap-1">
                {areasActivas?.length > 0
                  ? areasActivas?.map(a => <AreaBadge key={a} area={a} />)
                  : <span className="font-oranienbaum text-white/25 text-xs">Sin área</span>}
              </div>

              {/* Contacto */}
              <div className="md:col-span-2 flex flex-col justify-center gap-0.5">
                {c?.telefono && (
                  <span className="font-oranienbaum text-white/60 text-xs flex items-center gap-1">
                    <Icon name="Phone" size={10} className="text-white/30" />
                    {c?.telefono}
                  </span>
                )}
                {c?.email && (
                  <span className="font-oranienbaum text-white/40 text-xs flex items-center gap-1 truncate">
                    <Icon name="Mail" size={10} className="text-white/30" />
                    {c?.email}
                  </span>
                )}
                {!c?.telefono && !c?.email && <span className="font-oranienbaum text-white/25 text-xs">—</span>}
              </div>

              {/* Última actividad */}
              <div className="md:col-span-1 flex items-center">
                <span className="font-oranienbaum text-white/35 text-xs">{formatFecha(c?.updated_at)}</span>
              </div>

              {/* Estado */}
              <div className="md:col-span-1 flex items-center">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-oranienbaum text-xs font-semibold`}
                  style={c?.activo
                    ? { background: 'rgba(52,211,153,0.10)', color: '#34D399', border: '1px solid rgba(52,211,153,0.20)' }
                    : { background: 'rgba(239,68,68,0.08)', color: '#F87171', border: '1px solid rgba(239,68,68,0.20)' }}>
                  <span className={`w-1.5 h-1.5 rounded-full ${c?.activo ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  {c?.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              {/* Acciones */}
              <div className="md:col-span-1 flex items-center justify-end gap-1" onClick={e => e?.stopPropagation()}>
                <button onClick={() => handleEditar(c)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-white/10"
                  title="Editar">
                  <Icon name="Pencil" size={13} className="text-white/40 hover:text-white/80" />
                </button>
                <button onClick={() => handleToggleActivo(c)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-white/10"
                  title={c?.activo ? 'Desactivar' : 'Activar'}>
                  <Icon name={c?.activo ? 'EyeOff' : 'Eye'} size={13} className="text-white/40 hover:text-white/80" />
                </button>
                <button onClick={() => navigate(`/comercial-clientes/${c?.id}`)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-white/10"
                  title="Ver ficha">
                  <Icon name="ArrowRight" size={13} className="text-white/40 hover:text-white/80" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalOpen && (
        <ClienteModal
          cliente={clienteEditar}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default ComercialClientes;
